import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    const loadVideo = () => {
      for (const source of videoSources) {
        const { videoSource } = source.dataset;
        if (videoSource) source.setAttribute('src', videoSource);
      }

      videoElement.preload = 'metadata';
      videoElement.load();

      const playVideo = () => videoElement.play().catch(() => {});
      videoElement.addEventListener('canplay', playVideo, { once: true });
      if (videoElement.readyState >= 2) playVideo();
    };

    // Check if the element is already in the viewport (above-the-fold hero).
    // If so, defer video loading until after the page is idle so it doesn't
    // compete with LCP rendering and inflate TBT.
    const rect = this.getBoundingClientRect();
    const isAboveFold = rect.top < window.innerHeight;

    if (isAboveFold) {
      // Defer until the browser is idle to avoid blocking LCP / TBT
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadVideo, { timeout: 3000 });
      } else {
        setTimeout(loadVideo, 3000);
      }
    } else if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.#observer.disconnect();
            this.#observer = null;
            loadVideo();
          }
        },
        { rootMargin: '200px' }
      );
      this.#observer.observe(this);
    } else {
      loadVideo();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
